import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { getWhatsappTemplate } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { sendInstitutionEmail } from '@adornis/buchungsheld-db/mails/sendInstitutionMail.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { WorkshopDocument } from '../entities/WorkshopDocument.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.NEW_WORKSHOP_DOCUMENTS, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Neue Workshop-Dokumente verfügbar',
    content: `
            <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
            <p>für Ihren Workshop "{{product-name}}" wurden neue Dokumente hinzugefügt:</p>
            <p>{{document-links}}</p>
            <p>Sie können die Dokumente in Ihrem Workshop-Dashboard unter folgendem Link einsehen: {{login-link}}</p>
            <p>Mit freundlichen Grüßen</p>
            <p>---------------------------------------------------------------------</p>
            <p>Hello {{title}} {{firstname}} {{lastname}},</p>
            <p>new documents have been added to your workshop "{{product-name}}":</p>
            <p>{{document-links}}</p>
            <p>You can view the documents in your workshop dashboard using the following link: {{login-link}}</p>
            <p>Best regards</p>
            `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.NEW_WORKSHOP_DOCUMENTS),
  attachments: [],
  displayName: 'Neues Workshop-Dokument',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.NEW_WORKSHOP_DOCUMENTS),
});

export async function notifyNewWorkshopDocument(participationID: string, documentIDs: string[]) {
  const participation = await getByID(
    Participation,
    participationID,
  )({ _id: 1, productID: 1, userID: 1, timeSlotID: 1 });
  if (!participation) throw new Error('participation not found ' + participationID);

  const workshop = await getByID(
    Workshop,
    participation.productID,
  )({ _id: 1, name: 1, institutionID: 1, appointments: { start: 1, end: 1 } });
  if (!workshop) throw new Error('workshop not found ' + participation.productID);

  const workshopDocuments = await getByID(
    WorkshopDocument,
    documentIDs,
  )({ _id: 1, title: 1, text: 1, fileID: 1, workshopID: 1 });
  if (!workshopDocuments) throw new Error('workshop document not found ' + participation.productID);
  const fileIDs = workshopDocuments.map(doc => doc.fileID);

  const institution = await getByID(
    Institution,
    workshop.institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found ' + workshop.institutionID);

  const mailTemplateType = MailTemplateType.NEW_WORKSHOP_DOCUMENTS;

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    workshop.institutionID,
    mailTemplateType,
  )(InstitutionMailTemplate.allFields);

  const user = await getByID(
    User,
    participation.userID,
  )({ _id: 1, title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 });
  if (!user) throw new Error('user not found ' + participation.userID);

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  // Get notification type
  const notificationType = getNotificationType(user, mailTemplate);

  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone!,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(mailTemplateType).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: workshop.name || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.PRODUCT_NAME]: workshop.name,
      [MailTemplateVars.DOCUMENT_LINKS]: fileIDs
        .map(fileID => AdornisFile.getServeLink(fileID))
        .map(link => `<a href="${link}">Hier herunterladen</a>`)
        .join('<br />'),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
    };

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      institutionID: institution._id,
      email: user.email,
      subject,
      content,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
